import { useContext, useEffect, useRef } from 'react'

import { toast } from 'react-hot-toast/headless'
import { TransProps } from 'react-i18next'
import { useLoaderData } from 'react-router'

import { NotificationContext } from '~/shared/components/Notifications/context.ts'
import { NotificationConfig, NotificationType, SessionNotificationConfig, SessionNotificationMessage } from '~/shared/types/notifications.ts'

const NOTIFICATION_TIMEOUT = 500

const defaultNotificationConfig: Partial<NotificationConfig> = {
  dismiss: false,
}

const mapMessage = (message: SessionNotificationMessage, type: NotificationType, appMode: string): TransProps<string> => ({
  i18nKey: `${appMode}StatusCodes.${type}.${message.code}`,
  values: message?.values,
})

export const useNotificationObserver = () => {
  const { appMode, notification: sessionNotification } = useLoaderData<{ appMode: string; notification?: SessionNotificationConfig | null }>()

  const { notification, setNotification } = useContext(NotificationContext)

  const ref = useRef<string>('')

  /**
   * React in strict mode triggers two render cycles on application startup to trap and resolve and side-effects.
   * This has a knock-on effect of causing initial hooks to be called twice.
   * In this case, this pops up the notification message twice 
   * as the renders are looking to loader data without asking for new content
   */

  const clearRefOnDismissNotification = () => {
    if (sessionNotification?.duration || notification?.duration) {
      ref.current = ''
      setNotification(null)
    }
  }

  const toastNotification = (notification: NotificationConfig) => {
    const notificationString = JSON.stringify(notification)

    if (notification && notificationString !== ref.current) {

      const duration = notification?.duration === 'Infinity' ? Infinity : notification?.duration

      toast(notification as any, { duration: duration, id: notification.id })

      ref.current = notificationString

      if (duration && !isFinite(duration)) {
        return
      }

      setTimeout(() => {
        ref.current = ''
      }, duration || NOTIFICATION_TIMEOUT)
    }
  }

  useEffect(() => {
    if (sessionNotification) {
      toastNotification({
        ...defaultNotificationConfig,
        ...sessionNotification,
        message: mapMessage(sessionNotification.message, sessionNotification.type, appMode),
      } as NotificationConfig)
    }
  }, [sessionNotification])

  useEffect(() => {
    if (notification) {
      toastNotification({
        ...defaultNotificationConfig,
        ...notification,
      })
    }
  }, [notification])

  return { clearRefOnDismissNotification }
}